import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tab, TabPanel, Tabs, TabList } from "react-tabs";
import { get } from "lodash";
import "../../../style/react-tabs-filters.css";
import styled from "styled-components/macro";
import { animated, useSpring } from "react-spring";

import { Filter, FilterValue } from "../../../store/models/filter";
import { SET_ITEM_FILTER } from "../../../store/actions/filterAction";
import RemoveFilterIcon from "../../../icons/clearIconWhite.png";

import { getAppConfig, getLanguage, getAccountFilterTexts, getItemFilter } from "../../../store/sagas/selectors";

import CustomScrollbar from "../../CustomScrollbar/CustomScrollbar";

interface TabProps {
    ismobile?: Boolean;
}

const StyledTabPanel = styled(TabPanel)`
    display: flex;
    flex-flow: row wrap;
    align-items: flex-start;
    justify-content: flex-start;
    max-width: 100%;
    font-size: 13px;
    width: calc(100% - 18px);
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const FilterValueContainer = styled.div`
    display: flex;
    flex-flow: row wrap;
    margin: 3px 0px;
    justify-content: center;
    align-items: center;
`;

const FilterValueLabel = styled.label`
    padding: 4px 0px;
    cursor: pointer;
`;

const FilterValueName = styled.span`
    margin: 5px;
    border-radius: 50px;
    padding: 4px 10px;
    background-color: #d9d9d9;
    transition: background-color 0.2s ease-in-out;
    @media (hover) {
        &:hover {
            background-color: ${({ theme }) => get(theme, "filter.chips.hoverColor", "#223861")};
            ${({ theme }) =>
                theme.bp.sm`
                    background-color:#d9d9d9;
                    `}
            ${({ theme }) =>
                theme.bp.xs`
                    background-color:#d9d9d9;
                    `}
        }
    }
`;

const FilterValueCheckbox = styled.input`
    display: none;
    &:checked + ${FilterValueName} {
        background-color: ${({ theme }) => get(theme, "filter.chips.backgroundColor", "#223861")};
        color: #fff;
        &:hover {
            background-color: ${({ theme }) => get(theme, "buttonBack.hoverColor", "#223861")};
        }
    }
`;

const FilterBackground = styled.div<TabProps>`
    background-color: rgba(242, 242, 242, 1);
    border-radius: 0px 0px 5px 5px;
    border-top: solid thin #bfbfbf;
    padding: 7px 0px 7px 7px;
    backdrop-filter: ${props => (props.ismobile ? "blur(5px)" : "none")};
    max-height: ${props => (props.ismobile ? "261px" : "321px")};
    font-size: 13px;

    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}

    /* Remove white rectangle below focused tab */
    & .react-tabs__tab:focus:after {
        background: none;
    }
`;

const NumberOfFilters = styled.div`
    background: ${props => props.theme.buttonNext.backgroundColor};
    color: white;
    border-radius: 50px;
    width: fit-content;
    float: right;
    padding: 4px;
    font-size: 12px;
    margin-left: 5px;
    margin-top: 5px;

    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const ClearFilterIcon = styled.img`
    color: ${props => props.theme.textColorSecondary};
    height: 12px;
    margin-bottom: -2px;
`;

const TabContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const TabListContainer = styled.div`
    max-height: 81px;
`;

const ClearFilterButton = styled.div`
    background: ${props => props.theme.buttonNext.backgroundColor};
    color: white;
    border-radius: 50px;
    width: fit-content;
    float: right;
    padding: 4px 7px;
    font-size: 12px;
    margin: 3px;

    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

export const FilterControls = ({
    filters,
    filterActive,
    isMobile,
    dynamicType,
}: {
    filters: Filter[];
    filterActive: boolean;
    isMobile: boolean;
    dynamicType: string;
}) => {
    const dispatch = useDispatch();
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const accountFilterTexts = useSelector(getAccountFilterTexts);
    const itemFilter = useSelector(getItemFilter);

    const handleFilterCheckedChange = (key: string, value: string, checked: boolean) => {
        const newItemFilter = itemFilter.clone();
        newItemFilter.setTag(key, value, checked);
        dispatch({ type: SET_ITEM_FILTER, itemFilter: newItemFilter });
    };

    const isMobileMaxHeight = isMobile ? 265 : 300;
    const isMobileMaxScrollHeight = isMobile ? "140px" : "190px";

    const setHeight = useSpring({
        overflow: "hidden",
        maxHeight: filterActive ? isMobileMaxHeight : 0,
    });

    const shouldRenderRelative = true;

    const renderTabPanel = (filterValues: FilterValue[], key: string) => {
        return (
            <StyledTabPanel key={key}>
                {filterValues.map((filter: FilterValue) =>
                    filter.productCount > 0 || itemFilter.hasTagValue(key, filter.value) ? (
                        <FilterValueContainer key={filter.value}>
                            <FilterValueLabel>
                                <FilterValueCheckbox
                                    title={filter.value}
                                    checked={itemFilter.hasTagValue(key, filter.value)}
                                    type="checkbox"
                                    onChange={e => handleFilterCheckedChange(key, filter.value, e.target.checked)}
                                />
                                <FilterValueName>
                                    {filter.displayName} ({filter.productCount})
                                </FilterValueName>
                            </FilterValueLabel>
                        </FilterValueContainer>
                    ) : null
                )}
            </StyledTabPanel>
        );
    };

    const getFilterTabText = (filter: Filter) => {
        // Note: Cannot use lodash "get" if the filter name have special characters.
        return (
            accountFilterTexts[dynamicType]?.[filter.name] ||
            (language && appConfig.translations[language]?.[`filter_${filter.name}`]) ||
            filter.name
        );
    };

    const clearFilters = () => {
        const newItemFilter = itemFilter.clone();
        newItemFilter.clear();
        dispatch({ type: SET_ITEM_FILTER, itemFilter: newItemFilter });
    };

    return (
        <animated.div style={setHeight}>
            <FilterBackground ismobile={isMobile}>
                <Tabs>
                    <TabContainer>
                        <TabListContainer>
                            <TabList>
                                {filters.map(filter => (
                                    <Tab key={filter.name}>
                                        {isMobile && itemFilter.getTagValues(filter.name).length > 0 ? (
                                            <NumberOfFilters />
                                        ) : (
                                            <></>
                                        )}
                                        {getFilterTabText(filter)}
                                    </Tab>
                                ))}

                                {isMobile && itemFilter.numActiveFilterValues() > 0 ? (
                                    <ClearFilterButton onClick={() => clearFilters()}>
                                        {get(appConfig, `translations.${language}.clearFilters`)}{" "}
                                        <ClearFilterIcon src={RemoveFilterIcon} />
                                    </ClearFilterButton>
                                ) : (
                                    <></>
                                )}
                            </TabList>
                        </TabListContainer>
                        <CustomScrollbar maxHeight={isMobileMaxScrollHeight} renderRelative={shouldRenderRelative}>
                            {filters.map(filter => renderTabPanel(filter.values, filter.name))}
                        </CustomScrollbar>
                    </TabContainer>
                </Tabs>
            </FilterBackground>
        </animated.div>
    );
};
